'use client';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MapPin,
  Plane,
  ShieldCheck,
  Star,
  Users,
  BriefcaseBusiness,
  Car,
  Route,
  CalendarCheck,
  MessageCircle,
  ChevronDown,
  Building2,
  Compass,
  BookOpenText,
  KeyRound,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const createWhatsAppUrl = (vehicle = '') => {
  const message = `Halo NAYVERA INDO TRANSPORT,

Saya ingin menanyakan ketersediaan sewa mobil Cirebon.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan Sopir / Lepas Kunci
Tanggal mulai:
Jam mulai:
Tanggal selesai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi mengenai:
• Ketersediaan kendaraan
• Harga dan pilihan paket
• Fasilitas yang didapat
• Syarat dan ketentuan
• Biaya tambahan jika ada

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
};

const fleet = [
  {
    name: 'Toyota Agya / Daihatsu Ayla',
    category: 'City Car',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '5 Penumpang',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    features: ['Power Steering', 'AC Dingin', 'Audio Bluetooth', 'Eco Mode'],
    price: 'Rp 290.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Pilihan hemat dan lincah untuk berkeliling Kota Cirebon, wisata kuliner, serta mobilitas harian.',
  },
  {
    name: 'Toyota Avanza',
    category: 'Family MPV',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/Avanza.jpeg',
    features: ['AC Double Blower', 'Audio Sistem', 'Kabin Luas', '+1 lagi'],
    price: 'Rp 500.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Mobil MPV terfavorit untuk perjalanan keluarga, ziarah, maupun agenda kunjungan di wilayah Cirebon.',
  },
  {
    name: 'Toyota Innova Reborn',
    category: 'Family & Business',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel / Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/REBORN.jpeg',
    features: ['AC Double Blower', 'Kabin Nyaman', 'Captain Seat opt', '+1 lagi'],
    price: 'Rp 700.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Nyaman dan bertenaga untuk perjalanan antarkota via Tol Cipali serta agenda bisnis korporat.',
  },
  {
    name: 'Toyota Innova Zenix',
    category: 'Executive MPV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Bensin / Hybrid',
    capacity: '7 Penumpang',
    image: '/assets/images/ZENIX G.webp',
    features: ['Panoramic Roof', 'Captain Seat', 'Electric Parking', '+1 lagi'],
    price: 'Rp 850.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'MPV kelas atas berteknologi canggih untuk memberikan kenyamanan ekstra bagi perjalanan tamu VIP.',
  },
  {
    name: 'Toyota Fortuner',
    category: 'Premium SUV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel',
    capacity: '7 Penumpang',
    image: '/assets/images/Fortuner.jpeg',
    features: ['4x2 / 4x4', 'Leather Seat', 'Push Start Button', '+1 lagi'],
    price: 'Rp 1.600.000',
    priceLabel: '12 jam · dengan driver',
    description: 'SUV tangguh untuk mendukung perjalanan kedinasan, acara formal, dan perjalanan jarak jauh.',
  },
  {
    name: 'Toyota Hiace Premio',
    category: 'Premium Van',
    status: 'Tersedia',
    transmission: 'Manual',
    fuel: 'Diesel',
    capacity: '14 Penumpang',
    image: '/assets/images/Hiace-Premio.jpeg',
    features: ['High Roof', 'AC per Kepala', 'Reclining Seat', '+1 lagi'],
    price: 'Rp 1.800.000',
    priceLabel: '12 jam · dengan driver',
    description: 'Kabin luas berkapasitas besar sangat ideal untuk rombongan wisata batik Trusmi, ziarah, atau outing kantor.',
  },
];

const services = [
  {
    icon: KeyRound,
    title: 'Sewa Mobil Lepas Kunci Cirebon',
    description:
      'Solusi transportasi mandiri bagi Anda yang ingin berkendara bebas privasi di area Kota dan Kabupaten Cirebon. Proses verifikasi mudah.',
  },
  {
    icon: Users,
    title: 'Sewa Mobil dengan Driver',
    description:
      'Perjalanan makin tenang bersama pengemudi lokal berpengalaman yang paham tempat wisata, restoran khas, dan jalur tercepat.',
  },
  {
    icon: Plane,
    title: 'Antar Jemput Stasiun & Bandara',
    description:
      'Layanan penjemputan tepat waktu dari Stasiun Cirebon Kejaksan/Prujakan maupun Bandara Kertajati Majalengka.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Transportasi Bisnis & Perjalanan Dinas',
    description:
      'Dukungan mobilisasi prima untuk meeting bisnis, kunjungan proyek pabrik, maupun agenda kedinasan instansi di Cirebon.',
  },
  {
    icon: Route,
    title: 'Carter Antarkota via Tol Cipali',
    description:
      'Layanan antar kota dari Cirebon ke Jakarta, Bandung, Semarang, Surabaya, atau kota-kota lainnya sesuai jadwal Anda.',
  },
  {
    icon: Compass,
    title: 'Wisata Kuliner, Budaya & Ziarah',
    description:
      'Keliling destinasi bersejarah Cirebon seperti Keraton Kasepuhan, Goa Sunyaragi, Batik Trusmi, hingga ziarah Sunan Gunung Jati.',
  },
];

const useCases = [
  {
    icon: CalendarCheck,
    title: 'Wisata Kuliner & Harian',
    description:
      'Pilihan fleksibel untuk berburu Empal Gentong, Nasi Jamblang, dan oleh-oleh khas Cirebon tanpa repot.',
  },
  {
    icon: Building2,
    title: 'Agenda Dinas & Bisnis',
    description:
      'Mendukung mobilitas rapat instansi pemerintah, swasta, serta rombongan kunjungan industri.',
  },
  {
    icon: Plane,
    title: 'Penjemputan Stasiun / Bandara',
    description:
      'Driver kami siap menyambut kedatangan Anda di pintu keluar Stasiun Kejaksan/Prujakan tepat waktu.',
  },
  {
    icon: Users,
    title: 'Ziarah & Rombongan Keluarga',
    description:
      'Tersedia armada Hiace berkapasitas 14 orang untuk agenda rombongan keluarga besar atau majelis ta\'lim.',
  },
  {
    icon: Star,
    title: 'Trip Wisata Sejarah & Batik',
    description:
      'Nikmati pengalaman seru mengunjungi Keraton Kasepuhan, Keraton Kanoman, hingga berbelanja batik di Desa Trusmi.',
  },
  {
    icon: Route,
    title: 'Perjalanan Lintas Provinsi',
    description:
      'Layanan perjalanan jarak jauh aman dan nyaman berangkat langsung dari Cirebon via Tol Cipali / Palimanan.',
  },
];

const areaGroups = [
  {
    title: 'Kota Cirebon',
    description: 'Cakupan cepat di pusat kota.',
    places: [
      'Kejaksan',
      'Lemahwungkuk',
      'Pekalipan',
      'Kesambi',
      'Harjamukti',
      'Kawasan Grage & CSB Mall',
    ],
  },
  {
    title: 'Kabupaten Cirebon',
    description: 'Menjangkau area industri & wisata.',
    places: [
      'Plered (Batik Trusmi)',
      'Sumber (Pusat Pemkab)',
      'Palimanan',
      'Gunung Jati',
      'Kedawung',
      'Ciledug & Losari',
    ],
  },
  {
    title: 'Stasiun & Hub Transportasi',
    description: 'Akses penjemputan utama.',
    places: [
      'Stasiun Cirebon Kejaksan',
      'Stasiun Cirebon Prujakan',
      'Terminal Harjamukti',
      'Gerbang Tol Plumbon',
      'Gerbang Tol Ciperna',
      'Gerbang Tol Palimanan',
    ],
  },
  {
    title: 'Sekitar Cirebon & Antarkota',
    description: 'Konektivitas ke kota tetangga.',
    places: [
      'Bandara Kertajati (BIJB)',
      'Kuningan (Linggarjati/Palutungan)',
      'Majalengka',
      'Indramayu',
      'Bandung via Tol Cisumdawu',
      'Jakarta via Tol Cipali',
    ],
  },
];

const reasons = [
  {
    icon: Car,
    title: 'Armada Terawat & Bersih',
    text: 'Seluruh kendaraan rutin diservis dan dijaga kebersihannya untuk memberikan kenyamanan maksimal.',
  },
  {
    icon: MessageCircle,
    title: 'Konsultasi Mudah via WhatsApp',
    text: 'Respon cepat dari admin kami untuk mendiskusikan rute, durasi sewa, dan penyesuaian anggaran.',
  },
  {
    icon: MapPin,
    title: 'Driver Paham Rute Wilayah Cirebon',
    text: 'Pengemudi ramah yang menguasai jalanan Cirebon, tempat kuliner legendaris, serta jalur wisata Kuningan.',
  },
  {
    icon: ShieldCheck,
    title: 'Harga Transparan & Amanah',
    text: 'Seluruh rincian tarif dan ketentuan disampaikan secara jujur dan transparan tanpa ada hidden fee.',
  },
];

const articles = [
  {
    number: '01',
    category: 'Panduan Wisata',
    title: 'Tips Memilih Sewa Mobil Cirebon untuk Trip Kuliner & Wisata Budaya',
    paragraphs: [
      'Kota Cirebon terkenal sebagai destinasi wisata budaya dan kuliner yang kaya. Mulai dari kelezatan Nasi Jamblang Mang Dul, Empal Gentong H. Apud, hingga keanggunan arsitektur Keraton Kasepuhan dan Pusat Batik Trusmi.',
      'Agar perjalanan eksplorasi kuliner Anda berjalan fleksibel tanpa harus berganti-ganti transportasi umum, menggunakan jasa sewa mobil di Cirebon dari NAYVERA adalah langkah yang sangat praktis.',
      'Pengemudi kami siap mengantar Anda berpindah dari satu titik lokasi ke titik lainnya secara efisien, sehingga waktu liburan Anda di Cirebon menjadi lebih menyenangkan.',
    ],
  },
  {
    number: '02',
    category: 'Solusi Korporat',
    title: 'Sewa Mobil Cirebon untuk Perjalanan Bisnis & Kunjungan Dinas Instansi',
    paragraphs: [
      'Sebagai salah satu pusat pertumbuhan ekonomi di pesisir utara Jawa Barat, Cirebon sering menjadi destinasi kunjungan kerja instansi pemerintah, perguruan tinggi, maupun sektor industri manufaktur.',
      'NAYVERA INDO TRANSPORT menyediakan deretan mobil eksklusif seperti Toyota Innova Reborn, Zenix, dan Fortuner untuk menjamin mobilitas eksekutif yang elegan dan tepat waktu.',
      'Dengan pengemudi yang disiplin serta memahami tata krama pelayanan korporat, agenda rapat bisnis dan peninjauan lapangan Anda dapat terlaksana dengan sukses.',
    ],
  },
  {
    number: '03',
    category: 'Lepas Kunci vs Sopir',
    title: 'Keuntungan Rental Mobil Lepas Kunci dan Dengan Sopir di Cirebon',
    paragraphs: [
      'Pilihan lepas kunci sangat ideal bagi Anda yang menginginkan kebebasan privasi penuh saat berkendara di Cirebon, terutama jika Anda sudah familiar dengan arah jalanan kota.',
      'Namun bagi Anda yang tiba menggunakan kereta api di Stasiun Kejaksan dan ingin fokus bersantai bersama keluarga, memilih paket sewa mobil lengkap dengan pengemudi adalah opsi yang jauh lebih nyaman.',
      'Anda cukup menginformasikan detail rencana perjalanan kepada tim kami, dan kami akan membantu menyiapkan opsi layanan terbaik.',
    ],
  },
  {
    number: '04',
    category: 'Antar Jemput Stasiun',
    title: 'Panduan Praktis Layanan Pick-Up Stasiun Kejaksan & Prujakan Cirebon',
    paragraphs: [
      'Stasiun Cirebon Kejaksan dan Prujakan menjadi gerbang utama kedatangan wisatawan maupun pelaku bisnis dari Jakarta, Bandung, dan Jawa Tengah.',
      'Menggunakan layanan antar jemput khusus dari NAYVERA membuat Anda tidak perlu lagi mengantre taksi atau bingung mencari kendaraan saat mendarat di stasiun.',
      'Pengemudi kami akan menunggu kedatangan kereta Anda di pintu penjemputan dan siap langsung mengantar menuju hotel, tempat meeting, atau lokasi wisata pilihan Anda.',
    ],
  },
  {
    number: '05',
    category: 'Rekomendasi Rombongan',
    title: 'Sewa Hiace Premio di Cirebon untuk Agenda Ziarah & Gathering Kantor',
    paragraphs: [
      'Menyelenggarakan perjalanan ziarah ke Kompleks Makam Sunan Gunung Jati atau outing kantor di kawasan Cirebon dan Kuningan akan lebih berkesan saat rombongan dapat bepergian dalam satu kendaraan besar.',
      'Toyota Hiace Premio menghadirkan kelapangan kabin, AC yang sejuk hingga baris paling belakang, serta kursi yang nyaman untuk menjaga kondisi fisik rombongan.',
      'Diskusikan jumlah penumpang serta rute tempat wisata yang ingin dikunjungi agar kami dapat menyusun armada terbaik untuk agenda Anda.',
    ],
  },
  {
    number: '06',
    category: 'Tips Pemesanan',
    title: 'Prosedur Mudah Booking Mobil di NAYVERA INDO TRANSPORT Cirebon',
    paragraphs: [
      'Pemesanan kendaraan di NAYVERA dirancang sangat mudah via layanan pesan instan WhatsApp tanpa proses administrasi yang rumit.',
      'Kirimkan detail tanggal pemakaian, lokasi penjemputan (misal: Stasiun Kejaksan / Hotel), durasi sewa, serta jenis mobil yang diminati.',
      'Customer service kami akan langsung mengonfirmasi ketersediaan armada beserta rincian biaya yang transparan sebelum konfirmasi pemesanan diterbitkan.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa kisaran harga sewa mobil di Cirebon bersama NAYVERA?',
    answer:
      'Tarif sewa mobil di Cirebon mulai dari 300 ribuan tergantung jenis kendaraan, durasi sewa, serta pilihan lepas kunci atau menggunakan pengemudi.',
  },
  {
    question: 'Apakah tersedia sewa mobil Cirebon lepas kunci?',
    answer:
      'Ya, kami menyediakan opsi lepas kunci untuk beberapa tipe mobil dengan syarat penyerahan dokumen verifikasi identitas resmi demi keamanan bersama.',
  },
  {
    question: 'Apakah bisa dijemput langsung di Stasiun Cirebon Kejaksan / Prujakan?',
    answer:
      'Sangat bisa. Kami menyediakan layanan antar-jemput stasiun secara tepat waktu sehingga perjalanan Anda berlanjut dengan nyaman begitu turun dari kereta.',
  },
  {
    question: 'Wilayah mana saja yang masuk dalam jangkauan sewa mobil Cirebon?',
    answer:
      'Layanan kami mencakup seluruh Kota Cirebon, Kabupaten Cirebon, kawasan wisata Batik Trusmi, hingga wilayah tetangga seperti Kuningan, Majalengka, dan Indramayu.',
  },
  {
    question: 'Apakah mobil bisa disewa untuk trip wisata ke Kuningan (Palutungan/Linggarjati)?',
    answer:
      'Bisa sekali. Banyak pelanggan yang menyewa armada kami dari Cirebon untuk berwisata sejuk ke daerah pegunungan Kuningan.',
  },
  {
    question: 'Apakah melayani penjemputan dari Bandara Kertajati (BIJB)?',
    answer:
      'Tentu saja! Kami melayani antar jemput dari Bandara Internasional Jawa Barat Kertajati Majalengka menuju Cirebon maupun sebaliknya.',
  },
  {
    question: 'Bagaimana cara konfirmasi pemesanan (booking)?',
    answer:
      'Anda cukup menghubungi kami via WhatsApp, menyampaikan detail tanggal, jam, alamat penjemputan, serta unit yang diminati. Admin kami akan memprosesnya dengan cepat.',
  },
  {
    question: 'Apakah biaya tol, parkir, dan BBM sudah termasuk dalam harga?',
    answer:
      'Tergantung pada jenis paket yang dipilih (All-In atau lepas kunci/driver saja). Komponen rincian biaya akan diinformasikan secara terbuka oleh admin.',
  },
];

export default function SewaMobilCirebonPage() {
  const whatsappUrl = createWhatsAppUrl();

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#07111f]">
        <div className="absolute inset-0">
          <img
            src="/assets/images/AYLA DAN AGYA.jpg"
            alt="Sewa mobil Cirebon NAYVERA INDO TRANSPORT"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07111f] via-[#07111f]/95 to-[#07111f]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[1.15fr_.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-2 text-sm font-medium text-[#e4c45a]">
              <MapPin size={16} />
              Sewa Mobil Cirebon & Sekitarnya
            </div>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sewa Mobil Cirebon
              <span className="mt-2 block text-[#d4af37]">
                Mulai dari 300 Ribuan Terpercaya
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              NAYVERA INDO TRANSPORT menyediakan solusi rental mobil profesional
              di Cirebon untuk kebutuhan wisata kuliner & batik, penjemputan stasiun,
              perjalanan dinas, ziarah, hingga carter luar kota via Tol Cipali.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={19} />
                Konsultasi & Booking WhatsApp
                <ArrowRight size={18} />
              </a>

              <a
                href="#armada"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 font-semibold text-white backdrop-blur transition hover:border-[#d4af37]/60 hover:bg-white/10"
              >
                Lihat Pilihan Armada
              </a>
            </div>

            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ['300 Ribuan', 'Tarif terjangkau*'],
                ['Lepas Kunci', 'Opsi fleksibel'],
                ['Dengan Sopir', 'Driver berpengalaman'],
                ['Cirebon Raya', 'Cakupan luas'],
              ].map(([value, label]) => (
                <div
                  key={label}
                  className="border-l border-[#d4af37]/50 pl-4"
                >
                  <div className="font-bold text-[#e4c45a]">{value}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-400">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
                  <Car size={25} />
                </div>

                <div>
                  <p className="font-bold text-white">Rencana Perjalanan di Cirebon?</p>
                  <p className="text-sm text-slate-400">
                    Konsultasikan kebutuhan Anda sekarang
                  </p>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan durasi sewa',
                  'Pilih armada sesuai jumlah penumpang',
                  'Tentukan titik jemput di Cirebon',
                  'Konfirmasikan harga & fasilitas',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="mt-0.5 shrink-0 text-[#d4af37]"
                    />
                    <p className="text-sm leading-6 text-slate-300">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-5 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={18} />
                Tanyakan Ketersediaan Unit
              </a>

              <p className="mt-4 text-center text-xs leading-5 text-slate-400">
                Respon cepat via WhatsApp oleh tim NAYVERA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div>
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Pusat Rental Mobil Cirebon
            </p>

            <h2 className="text-3xl font-bold leading-tight text-[#07111f] sm:text-4xl">
              Solusi Transportasi Terbaik di
              <span className="text-[#b89528]"> Kota & Kabupaten Cirebon</span>
            </h2>

            <p className="mt-6 text-base leading-8 text-slate-600">
              Mencari layanan rental mobil yang aman, terawat, dan tepat waktu di Cirebon? 
              NAYVERA INDO TRANSPORT hadir memberikan kemudahan transportasi bagi wisatawan 
              maupun pelaku bisnis yang berkunjung ke Kota Udang. Mulai dari city car yang lincah 
              hingga MPV keluarga dan van berkapasitas besar tersedia lengkap.
            </p>

            <p className="mt-4 text-base leading-8 text-slate-600">
              Nikmati opsi sewa mobil lepas kunci bagi Anda yang mengutamakan fleksibilitas mandiri, 
              atau manfaatkan layanan dengan pengemudi ramah yang siap mengantar Anda menjelajahi 
              kuliner khas, keraton bersejarah, hingga sentra kerajinan batik Trusmi.
            </p>
          </div>

          <div className="rounded-3xl bg-[#07111f] p-8 shadow-xl">
            <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
              <ShieldCheck size={25} />
            </div>

            <h3 className="text-2xl font-bold text-white">
              Keunggulan Layanan NAYVERA
            </h3>

            <div className="mt-7 space-y-4">
              {[
                'Armada terawat & kondisi selalu prima',
                'Opsi lepas kunci atau dengan sopir',
                'Jangkauan luas Cirebon & Kuningan',
                'Tarif transparan tanpa biaya tersembunyi',
                'Layanan respon cepat WhatsApp 24 jam',
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <CheckCircle2
                    size={20}
                    className="mt-0.5 shrink-0 text-[#d4af37]"
                  />
                  <span className="text-sm leading-6 text-slate-300">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#07111f] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Layanan NAYVERA di Cirebon
            </p>

            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Pilihan Jenis Layanan Rental Mobil
              <span className="text-[#d4af37]"> Sesuai Kebutuhan</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Sesuaikan kategori layanan kendaraan dengan agenda perjalanan Anda di Cirebon.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => {
              const Icon = service.icon;

              return (
                <article
                  key={service.title}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/10 text-[#e4c45a] transition group-hover:bg-[#d4af37] group-hover:text-[#07111f]">
                    <Icon size={23} />
                  </div>

                  <h3 className="mt-6 text-xl font-bold text-white">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    {service.description}
                  </p>

                  <a
                    href={createWhatsAppUrl(service.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                  >
                    Konsultasikan layanan ini
                    <ArrowRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Agenda Perjalanan
            </p>

            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Kendaraan Andal untuk
              <span className="text-[#b89528]"> Berbagai Keperluan</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#d4af37]/50 hover:shadow-lg"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={21} />
                  </div>

                  <h3 className="mt-5 text-lg font-bold text-[#07111f]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET / ARMADA */}
      <section id="armada" className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
                Katalog Armada
              </p>

              <h2 className="text-3xl font-bold text-[#07111f] sm:text-4xl">
                Pilihan Armada Sewa Mobil
                <span className="text-[#b89528]"> Cirebon</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-600">
                Berikut adalah deretan armada terbaik kami yang siap disewa untuk menemani 
                perjalanan Anda di Cirebon, Kuningan, dan sekitarnya.
              </p>
            </div>

            <a
              href="/fleet-armada-page"
              className="inline-flex items-center gap-2 font-semibold text-[#07111f] transition hover:text-[#b89528]"
            >
              Lihat Armada Lengkap
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {fleet.map((car) => (
              <article
                key={car.name}
                className="group flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/70 hover:shadow-xl"
              >
                <div>
                  <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100">
                    <img
                      src={car.image}
                      alt={`${car.name} untuk sewa mobil Cirebon NAYVERA`}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                      {car.status}
                    </div>

                    <div className="absolute right-3 top-3 rounded-full bg-[#d4af37] px-3 py-1 text-xs font-bold text-[#07111f] shadow-sm">
                      {car.category}
                    </div>

                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Users size={13} className="text-[#d4af37]" />
                      {car.capacity}
                    </div>
                  </div>

                  <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#b89528]">
                    {car.category}
                  </p>

                  <h3 className="mt-1 text-xl font-bold text-[#07111f]">
                    {car.name}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⚙️ {car.transmission}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⛽ {car.fuel}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {car.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
                      >
                        ✨ {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 border-t border-slate-100 pt-4">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Mulai dari</p>
                      <p className="text-lg font-black text-[#07111f]">{car.price}</p>
                      <p className="text-[11px] text-slate-400">{car.priceLabel}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-xl bg-[#d4af37] px-4 py-2 text-center text-xs font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
                      >
                        Booking
                      </a>
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1 rounded-xl border border-emerald-500/40 bg-emerald-50/50 px-3 py-1.5 text-center text-xs font-bold text-emerald-600 transition hover:bg-emerald-500/10"
                      >
                        <MessageCircle size={13} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-[#0b1726] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Cakupan Wilayah
            </p>

            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Area Layanan Rental Mobil
              <span className="text-[#d4af37]"> Cirebon & Sekitarnya</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Kami melayani penjemputan di seluruh titik Kota Cirebon, Kabupaten Cirebon, 
              stasiun kereta api, hingga penjemputan wilayah Ciayumajakuning.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {areaGroups.map((area) => (
              <article
                key={area.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
              >
                <div className="flex items-center gap-2 text-[#e4c45a]">
                  <MapPin size={18} />
                  <h3 className="font-bold text-white">{area.title}</h3>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-400">
                  {area.description}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {area.places.map((place) => (
                    <li
                      key={place}
                      className="border-b border-white/5 pb-2 text-sm text-slate-400 last:border-0"
                    >
                      {place}
                    </li>
                  ))}
                </ul>

                <a
                  href={createWhatsAppUrl(`Perjalanan area ${area.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                >
                  Tanyakan area ini
                  <ArrowRight size={15} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT & STASIUN */}
      <section className="overflow-hidden bg-[#07111f]">
        <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[520px]">
            <img
              src="/assets/images/Armada Hiace Nayvera.jpeg"
              alt="Armada NAYVERA untuk penjemputan Stasiun dan Bandara Cirebon"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#07111f]" />
          </div>

          <div className="flex items-center px-6 py-20 lg:px-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
                Stasiun & Airport Transfer
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">
                Penjemputan Stasiun Kejaksan
                <span className="text-[#d4af37]"> & Bandara Kertajati</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-300">
                Tiba di Cirebon lewat kereta api atau pesawat? NAYVERA siap menyambut kedatangan 
                Anda di Stasiun Cirebon Kejaksan/Prujakan maupun Bandara Kertajati Majalengka 
                dengan layanan profesional dan armada ber-AC sejuk.
              </p>

              <div className="mt-8">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
                >
                  <MessageCircle size={18} />
                  Pesan Penjemputan Stasiun/Bandara
                  <ArrowRight size={17} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UNIQUE SEO ARTICLES SECTION */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Wawasan & Panduan Sewa Mobil
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Artikel & Tips Seputar
              <span className="text-[#b89528]"> Rental Mobil di Cirebon</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Informasi lengkap untuk membantu merencanakan liburan kuliner, agenda bisnis, dan perjalanan keluarga Anda di Cirebon.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {articles.map((art) => (
              <article
                key={art.number}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-[#d4af37]/60 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#07111f] px-3.5 py-1 text-xs font-bold text-[#e4c45a]">
                    {art.category}
                  </span>
                  <span className="text-2xl font-black text-slate-300">
                    {art.number}
                  </span>
                </div>

                <h3 className="mt-5 text-xl font-bold text-[#07111f]">
                  {art.title}
                </h3>

                <div className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                  {art.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Kenapa Memilih Kami
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Alasan Percayakan Perjalanan Anda
              <span className="text-[#b89528]"> Kepada NAYVERA</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  className="rounded-2xl border border-slate-200 p-6 transition hover:border-[#b89528]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-bold text-[#07111f]">{r.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="mt-4 text-slate-600">
              Temukan jawaban seputar layanan sewa mobil Cirebon bersama NAYVERA.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition open:border-[#d4af37]"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-[#07111f]">
                  {faq.question}
                  <ChevronDown size={18} className="transition group-open:rotate-180 text-[#b89528]" />
                </summary>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER BANNER */}
      <section className="bg-[#07111f] py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
            Siap Berangkat ke Cirebon?
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Pesan Kendaraan Anda Sekarang di NAYVERA
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Dapatkan pelayanan cepat, ramah, dan transparan untuk setiap kebutuhan perjalanan Anda.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-8 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
            >
              <MessageCircle size={20} />
              Hubungi WhatsApp Sekarang
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}